import { z } from 'zod';
/**
 * Docs & Knowledge Base portal (BRD §13 / Docs Portal spec).
 *
 * Hierarchy: Space → Folder → Document → Page → Sub-page. Each page supports
 * ownership, multi-user role-based assignment, status, version history,
 * comments, tags, permissions, relations to tickets/sprints/releases, and an
 * activity log. Mirrors the `customer.ts` contract style: an enum + entity
 * schema + Create/Update inputs per resource.
 */
// --- Enums -----------------------------------------------------------------
export const DocVisibility = z.enum(['private', 'team', 'company']);
export const DocDocumentStatus = z.enum(['draft', 'in_review', 'published', 'archived']);
export const DocPageStatus = z.enum([
    'draft', 'in_progress', 'in_review', 'approved', 'published', 'archived',
]);
export const DocAssignmentRole = z.enum(['owner', 'writer', 'reviewer', 'approver', 'viewer']);
export const DocAssignmentStatus = z.enum(['pending', 'in_progress', 'completed']);
/** Access-control roles (permission matrix §10). */
export const DocAccessRole = z.enum(['owner', 'admin', 'editor', 'commenter', 'viewer']);
/** "none" is used in-memory by the access resolver; it is never persisted. */
export const DocEffectiveRole = z.enum(['owner', 'admin', 'editor', 'commenter', 'viewer', 'none']);
export const DocScopeType = z.enum(['space', 'folder', 'document', 'page']);
export const DocRelationTargetType = z.enum(['ticket', 'sprint', 'release', 'user', 'roadmap_item']);
export const DocRelationType = z.enum([
    'references', 'blocks', 'documents', 'created_from', 'related_to',
]);
// --- Space -----------------------------------------------------------------
export const DocSpace = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    icon: z.string().nullable(),
    color: z.string().nullable(),
    visibility: DocVisibility,
    sortOrder: z.number(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
});
export const CreateDocSpaceInput = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    icon: z.string().max(40).optional(),
    color: z.string().max(40).optional(),
    visibility: DocVisibility.optional(),
});
export const UpdateDocSpaceInput = CreateDocSpaceInput.partial().extend({
    sortOrder: z.number().optional(),
    archived: z.boolean().optional(),
});
// --- Folder ----------------------------------------------------------------
export const DocFolder = z.object({
    id: z.string(),
    spaceId: z.string(),
    parentFolderId: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
});
export const CreateDocFolderInput = z.object({
    spaceId: z.string().min(1),
    parentFolderId: z.string().optional(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
});
export const UpdateDocFolderInput = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    parentFolderId: z.string().nullable().optional(),
    spaceId: z.string().optional(),
    sortOrder: z.number().optional(),
    archived: z.boolean().optional(),
});
// --- Document --------------------------------------------------------------
export const DocDocument = z.object({
    id: z.string(),
    spaceId: z.string(),
    folderId: z.string().nullable(),
    title: z.string(),
    summary: z.string().nullable(),
    status: DocDocumentStatus,
    ownerId: z.string().nullable(),
    pinned: z.boolean(),
    sortOrder: z.number(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
});
export const CreateDocDocumentInput = z.object({
    spaceId: z.string().min(1),
    folderId: z.string().optional(),
    title: z.string().min(1).max(300),
    summary: z.string().max(4000).optional(),
    status: DocDocumentStatus.optional(),
    ownerId: z.string().optional(),
});
export const UpdateDocDocumentInput = z.object({
    title: z.string().min(1).max(300).optional(),
    summary: z.string().max(4000).nullable().optional(),
    status: DocDocumentStatus.optional(),
    ownerId: z.string().nullable().optional(),
    folderId: z.string().nullable().optional(),
    spaceId: z.string().optional(),
    pinned: z.boolean().optional(),
    sortOrder: z.number().optional(),
    archived: z.boolean().optional(),
});
// --- Page ------------------------------------------------------------------
export const DocPage = z.object({
    id: z.string(),
    documentId: z.string(),
    parentPageId: z.string().nullable(),
    title: z.string(),
    content: z.string(),
    contentJson: z.unknown().nullable(),
    status: DocPageStatus,
    assignedToId: z.string().nullable(),
    ownerId: z.string().nullable(),
    dueDate: z.string().nullable(),
    tags: z.array(z.string()),
    sortOrder: z.number(),
    currentVersion: z.number(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    publishedAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
});
export const CreateDocPageInput = z.object({
    documentId: z.string().min(1),
    parentPageId: z.string().optional(),
    title: z.string().min(1).max(300),
    content: z.string().optional(),
    contentJson: z.unknown().optional(),
    status: DocPageStatus.optional(),
    assignedToId: z.string().optional(),
    ownerId: z.string().optional(),
    dueDate: z.string().optional(),
    tags: z.array(z.string()).optional(),
});
export const UpdateDocPageInput = z.object({
    title: z.string().min(1).max(300).optional(),
    content: z.string().optional(),
    contentJson: z.unknown().optional(),
    status: DocPageStatus.optional(),
    assignedToId: z.string().nullable().optional(),
    ownerId: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),
    parentPageId: z.string().nullable().optional(),
    sortOrder: z.number().optional(),
    archived: z.boolean().optional(),
    /** When true, snapshot a version before applying content changes. */
    saveVersion: z.boolean().optional(),
    changeSummary: z.string().max(500).optional(),
});
export const MoveDocPageInput = z.object({
    documentId: z.string().optional(),
    parentPageId: z.string().nullable().optional(),
});
// --- Page version ----------------------------------------------------------
export const DocPageVersion = z.object({
    id: z.string(),
    pageId: z.string(),
    versionNumber: z.number(),
    title: z.string(),
    content: z.string(),
    contentJson: z.unknown().nullable(),
    changeSummary: z.string().nullable(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
});
export const CreateDocPageVersionInput = z.object({
    changeSummary: z.string().max(500).optional(),
});
// --- Page assignment -------------------------------------------------------
export const DocPageAssignment = z.object({
    id: z.string(),
    pageId: z.string(),
    userId: z.string(),
    role: DocAssignmentRole,
    status: DocAssignmentStatus,
    assignedById: z.string().nullable(),
    dueDate: z.string().nullable(),
    createdAt: z.string(),
});
export const CreateDocPageAssignmentInput = z.object({
    userId: z.string().min(1),
    role: DocAssignmentRole.optional(),
    dueDate: z.string().optional(),
});
export const UpdateDocPageAssignmentInput = z.object({
    role: DocAssignmentRole.optional(),
    status: DocAssignmentStatus.optional(),
    dueDate: z.string().nullable().optional(),
});
// --- Page comment ----------------------------------------------------------
export const DocPageComment = z.object({
    id: z.string(),
    pageId: z.string(),
    parentCommentId: z.string().nullable(),
    body: z.string(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    resolvedById: z.string().nullable(),
});
export const CreateDocPageCommentInput = z.object({
    body: z.string().min(1).max(8000),
    parentCommentId: z.string().optional(),
});
export const UpdateDocPageCommentInput = z.object({
    body: z.string().min(1).max(8000),
});
// --- Permission ------------------------------------------------------------
export const DocPermission = z.object({
    id: z.string(),
    scopeType: DocScopeType,
    scopeId: z.string(),
    userId: z.string(),
    role: DocAccessRole,
    createdById: z.string().nullable(),
    createdAt: z.string(),
});
export const CreateDocPermissionInput = z.object({
    scopeType: DocScopeType,
    scopeId: z.string().min(1),
    userId: z.string().min(1),
    role: DocAccessRole,
});
// --- Relation --------------------------------------------------------------
export const DocRelation = z.object({
    id: z.string(),
    sourceType: DocScopeType,
    sourceId: z.string(),
    targetType: DocRelationTargetType,
    targetId: z.string(),
    relationType: DocRelationType,
    createdById: z.string().nullable(),
    createdAt: z.string(),
});
export const CreateDocRelationInput = z.object({
    sourceType: DocScopeType,
    sourceId: z.string().min(1),
    targetType: DocRelationTargetType,
    targetId: z.string().min(1),
    relationType: DocRelationType.optional(),
});
// --- Activity --------------------------------------------------------------
export const DocActivity = z.object({
    id: z.string(),
    entityType: DocScopeType,
    entityId: z.string(),
    action: z.string(),
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
});
// --- Page attachment -------------------------------------------------------
export const DocPageAttachment = z.object({
    id: z.string(),
    pageId: z.string(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number(),
    objectKey: z.string(),
    url: z.string(),
    createdById: z.string().nullable(),
    createdAt: z.string(),
});
export const DocTreeNode = z.lazy(() => z.object({
    type: DocScopeType,
    id: z.string(),
    title: z.string(),
    icon: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    visibility: DocVisibility.optional(),
    status: z.string().nullable().optional(),
    assignedToId: z.string().nullable().optional(),
    children: z.array(DocTreeNode),
}));
// --- Search ----------------------------------------------------------------
export const DocSearchResult = z.object({
    type: DocScopeType,
    id: z.string(),
    title: z.string(),
    spaceId: z.string().nullable(),
    spaceName: z.string().nullable(),
    documentId: z.string().nullable(),
    status: z.string().nullable(),
    snippet: z.string().nullable(),
    assignedToId: z.string().nullable(),
    updatedAt: z.string(),
});
/**
 * Page detail bundle returned by GET /docs/pages/:id — the page plus the
 * breadcrumb trail and the caller's effective access role.
 */
export const DocBreadcrumb = z.object({
    spaceId: z.string(),
    spaceName: z.string(),
    documentId: z.string(),
    documentTitle: z.string(),
    folderId: z.string().nullable(),
    folderName: z.string().nullable(),
});
//# sourceMappingURL=docs.js.map